import React, { useEffect, useMemo, useState } from 'react'

import { createPortal } from 'react-dom'
import { MAX_NAVITEM_PER_LEVEL, MAX_NUM_SUBLEVEL, MobileSliderContext, MobileSliderContextType } from './MobileSliderContext'
import { MobileSliderTitle } from './MobileSliderTitle'
import { StyledListContainer, StyledModalBackground, StyledSliderNavigation } from './StyledNavigator'

export type MobileSliderWrapperProps = {
  children: React.ReactNode
  portal: string
  isOpen: boolean
  onClose: () => void
  onNavigationClose?: () => void
  initialTitle: string
}

export const MobileSliderWrapper: React.FC<MobileSliderWrapperProps> = ({ isOpen, onClose, onNavigationClose, initialTitle, children, portal = '#browser-menu' }) => {
  const [menuState, setMenuState] = useState<boolean[]>(Array.from<boolean>({ length: 1 + MAX_NUM_SUBLEVEL * MAX_NAVITEM_PER_LEVEL }).fill(false))
  const [menuStack, setMenuStack] = useState<[number, number][]>([])
  const [titleStack, setTitleStack] = useState<string[]>([initialTitle])
  const [portalElement, setPortalElement] = useState<Element | null>(null)
  const [shouldRender, setShouldRender] = useState(isOpen)

  // get portal
  useEffect(() => {
    const portalEl = document.querySelector(portal)

    setPortalElement(portalEl)
  }, [setPortalElement, portal])

  const mobileMenuContext: MobileSliderContextType = useMemo(() => ({
    portal: portalElement,
    menuState,
    menuStack,
    setMenuStack,
    setTitleStack,
    setMenuState,
    getItemIndex: (level: number, idx: number) => (MAX_NAVITEM_PER_LEVEL * Math.max(0, (level - 1)) + idx),
    setMenuOpen: (level: number, idx: number) => {
      const newMenuState = [...menuState]

      newMenuState[MAX_NAVITEM_PER_LEVEL * level + idx] = true
      setMenuState(newMenuState)

      if (menuStack[menuStack.length - 1]?.[0] === level && menuStack[menuStack.length - 1]?.[1] === idx) {
        return
      }

      setMenuStack(prev => [
        ...prev,
        [level + 1, idx],
      ])
    },
    setMenuClose: (level: number, idx: number) => {
      const newMenuState = [...menuState]

      newMenuState[MAX_NAVITEM_PER_LEVEL * Math.max(0, (level - 1)) + idx] = false
      setMenuState(newMenuState)
      setMenuStack(prev => prev.slice(0, -1))
      setTitleStack(prev => prev.slice(0, -1))

      if (onNavigationClose) {
        onNavigationClose()
      }
    },
  }), [menuState, portal, portalElement, onNavigationClose])

  // Allow for animation when opening and closing before completely unmounting
  useEffect(() => {
    if (isOpen) {
      setShouldRender(isOpen)
    } else {
      const animateTimeout = setTimeout(() => {
        setShouldRender(isOpen)
      }, 130)

      return () => {
        clearTimeout(animateTimeout)
      }
    }
  }, [isOpen])


  // Reset menu state when initial open or global close
  useEffect(() => {
    if (isOpen) {
      mobileMenuContext.setMenuOpen(0, 0)
    } else {
      setMenuState(Array.from<boolean>({ length: 1 + MAX_NUM_SUBLEVEL * MAX_NAVITEM_PER_LEVEL }).fill(false))
      setMenuStack([])
    }
  }, [isOpen])

  const navigationSlider = (
    <StyledSliderNavigation
      $isGlobalOpen={isOpen}
      $isOpen={isOpen}
      $isAnimateOut={false}
      $level={-1}
      $animationTime={140}
      data-scroll-lock-scrollable
    >
      <StyledListContainer>
        <MobileSliderTitle
          onClose={onClose}
        >
          {titleStack[titleStack.length - 1] ?? initialTitle}
        </MobileSliderTitle>
        {children}
      </StyledListContainer>
    </StyledSliderNavigation>
  )

  return (
    <>
      <MobileSliderContext.Provider value={mobileMenuContext}>
        {shouldRender && portalElement && (
          createPortal(navigationSlider, portalElement)
        )}
      </MobileSliderContext.Provider>
      {isOpen && <StyledModalBackground onClick={onClose} $isGlobalOpen={isOpen} />}
    </>
  )
}
